import React from 'react';
import styled from 'styled-components';

const ExamplesContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  background: #f9fafb;
  border-radius: 8px;
  margin-top: 20px;
`;

const ExampleTitle = styled.h3`
  color: #1f2937;
  margin-bottom: 16px;
  font-size: 18px;
`;

const ExampleSection = styled.div`
  margin-bottom: 24px;
  padding: 16px;
  background: white;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
`;

const ExampleDescription = styled.p`
  color: #6b7280;
  margin-bottom: 12px;
  font-size: 14px;
`;

const FormulaExample = styled.code`
  display: block;
  background: #f3f4f6;
  padding: 12px;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  color: #374151;
  margin: 8px 0;
  border: 1px solid #d1d5db;
`;

const ComputationExamples = () => {
  return (
    <ExamplesContainer>
      <ExampleTitle>💻 Computation Examples</ExampleTitle>
      
      <ExampleSection>
        <ExampleDescription>
          <strong>Basic Math Operations</strong><br />
          Perform simple arithmetic calculations using field values and operators.
        </ExampleDescription>
        <FormulaExample>{`{quantity} * {price}`}</FormulaExample>
        <FormulaExample>{`{base_amount} + {tax} + {shipping}`}</FormulaExample>
        <FormulaExample>{`({hours_worked} * {hourly_rate}) * 1.5`}</FormulaExample>
      </ExampleSection>

      <ExampleSection>
        <ExampleDescription>
          <strong>Business Calculations</strong><br />
          Common business formulas for pricing, discounts, and totals.
        </ExampleDescription>
        <FormulaExample>{`{subtotal} * (1 + {tax_rate})`}</FormulaExample>
        <FormulaExample>{`{original_price} * (1 - {discount_percent}/100)`}</FormulaExample>
        <FormulaExample>{`{monthly_rent} * {lease_months} + {deposit}`}</FormulaExample>
      </ExampleSection>

      <ExampleSection>
        <ExampleDescription>
          <strong>Statistical Functions</strong><br />
          Use built-in functions for averages, sums, and comparisons.
        </ExampleDescription>
        <FormulaExample>{`SUM({score1}, {score2}, {score3}, {score4})`}</FormulaExample>
        <FormulaExample>{`AVERAGE({test1}, {test2}, {test3})`}</FormulaExample>
        <FormulaExample>{`MAX({option1}, {option2}, {option3})`}</FormulaExample>
        <FormulaExample>{`MIN({price_a}, {price_b}, {price_c})`}</FormulaExample>
      </ExampleSection>

      <ExampleSection>
        <ExampleDescription>
          <strong>Conditional Logic</strong><br />
          Use IF statements for conditional calculations.
        </ExampleDescription>
        <FormulaExample>{`IF({age} >= 65, {base_amount} * 0.9, {base_amount})`}</FormulaExample>
        <FormulaExample>{`IF({membership_type} = "Premium", {base_fee} * 0.8, {base_fee})`}</FormulaExample>
        <FormulaExample>{`IF({quantity} > 100, {unit_price} * 0.95, {unit_price})`}</FormulaExample>
      </ExampleSection>

      <ExampleSection>
        <ExampleDescription>
          <strong>Advanced Calculations</strong><br />
          Complex formulas with multiple operations and functions.
        </ExampleDescription>
        <FormulaExample>{`ROUND({total_amount} * {tip_percentage}/100, 2)`}</FormulaExample>
        <FormulaExample>{`SQRT({a}^2 + {b}^2)`}</FormulaExample>
        <FormulaExample>{`POW(1 + {interest_rate}/100, {years}) * {principal}`}</FormulaExample>
      </ExampleSection>

      <ExampleSection>
        <ExampleDescription>
          <strong>Real-World Examples</strong><br />
          Practical formulas for common use cases.
        </ExampleDescription>
        <FormulaExample>{`{base_salary} + ({overtime_hours} * {hourly_rate} * 1.5)`}</FormulaExample>
        <FormulaExample>{`({room_length} * {room_width}) * {carpet_price_per_sqft}`}</FormulaExample>
        <FormulaExample>{`{loan_amount} * ({interest_rate}/100/12) * (1 + {interest_rate}/100/12)^{months} / ((1 + {interest_rate}/100/12)^{months} - 1)`}</FormulaExample>
      </ExampleSection>

      <ExampleSection>
        <ExampleDescription>
          <strong>Available Operators</strong><br />
          Mathematical operators you can use in formulas.
        </ExampleDescription>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', fontSize: '14px' }}>
          <div><code>+</code> Addition</div>
          <div><code>-</code> Subtraction</div>
          <div><code>*</code> Multiplication</div>
          <div><code>/</code> Division</div>
          <div><code>^</code> Power</div>
          <div><code>%</code> Modulo</div>
        </div>
      </ExampleSection>

      <ExampleSection>
        <ExampleDescription>
          <strong>Available Functions</strong><br />
          Built-in functions for advanced calculations.
        </ExampleDescription>
        <div style={{ fontSize: '14px', lineHeight: '1.6' }}>
          <div><code>SUM(a, b, c, ...)</code> - Sum of all arguments</div>
          <div><code>AVERAGE(a, b, c, ...)</code> - Average of all arguments</div>
          <div><code>MAX(a, b, c, ...)</code> - Maximum value</div>
          <div><code>MIN(a, b, c, ...)</code> - Minimum value</div>
          <div><code>ROUND(value, decimals)</code> - Round to decimal places</div>
          <div><code>IF(condition, true_value, false_value)</code> - Conditional logic</div>
          <div><code>ABS(value)</code> - Absolute value</div>
          <div><code>SQRT(value)</code> - Square root</div>
          <div><code>POW(base, exponent)</code> - Power function</div>
        </div>
      </ExampleSection>
    </ExamplesContainer>
  );
};

export default ComputationExamples;
